import React from "react";
import PortalLayout from "../views/layouts/PortalLayout";
import {
  AnonymousGuard,
  AuthenticationGuard,
  Page401,
  Page403,
  Page404,
  SecurityGuard,
  SecurityRole,
} from "@duosoftbg/nacid-components";
import HomePage from "../views/pages/HomePage";
import Sitemap from "../views/pages/sitemap/Sitemap";
import Contacts from "../views/pages/contacts/Contacts";
import Law from "../views/pages/law/Law";
import SiteMapEdit from "../views/pages/admin/console/sitemap/SitemapEdit";
import AdminConsoleLayout from "../views/layouts/AdminConsoleLayout";
import AdminConsole from "../views/pages/admin/console/AdminConsole";
import Registration from "../views/components/users/account/registration/Registration";
import { Navigate } from "react-router-dom";
import Feedback from "../views/pages/feedback/Feedback";
import FeedbackManagement from "../views/pages/admin/console/feedback/FeedbackManagement";
import EmailNotificationsManagement from "../views/pages/admin/console/emails/notifications/EmailNotificationsManagement";
import EmailTemplatesManagement from "../views/pages/admin/console/emails/templates/EmailTemplatesManagement";
import ContactsManagement from "../views/pages/admin/console/contacts/ContactsManagement";
import ForgottenPassword from "../views/pages/user/recovery/password/forgotten/ForgottenPassword";
import ResetPassword from "../views/pages/user/recovery/password/reset/ResetPassword";
import LawManagement from "../views/pages/admin/console/law/LawManagement";
import AccountActivation from "../views/pages/user/account/activation/AccountActivation";
import ForgottenUsername from "../views/pages/user/recovery/username/forgotten/ForgottenUsername";
import ActivationLink from "../views/pages/user/recovery/activation/link/ActivationLink";
import UsersManagement from "../views/pages/admin/console/users/UsersManagement";
import GroupsAndRolesManagement from "../views/pages/admin/console/groupsAndRoles/GroupsAndRolesManagement";
import GlobalMessageManagement from "../views/pages/admin/console/globalMessage/GlobalMessageManagement";
import MyAccountManagement from "../views/pages/account/myAccount/MyAccountManagement";
import EmailChange from "../views/pages/user/update/email/EmailChange";
import AdminConsoleManagement from "../views/pages/admin/console/AdminConsoleManagement";
import ReferenceDataManagement from "../views/pages/admin/console/referenceData/ReferenceDataManagement";
import ServiceDefinitionsManagement from "../views/pages/admin/console/serviceDefinitions/ServiceDefinitionsManagement";
import NationalUniversityManagement from "../views/pages/admin/console/nationalUniversity/NationalUniversityManagement";
import AdminUserEdit from "../views/pages/admin/console/users/edit/AdminUserEdit";
import AdminUserView from "../views/pages/admin/console/users/view/AdminUserView";
import HomePageCategoryManagement from "../views/pages/admin/console/homePageCategoryControl/HomePageCategoryManagement";
import PersonalContent from "../views/pages/user/personalContent/PersonalContent";
import CachePage from "../views/pages/admin/console/cache/CachePage";
import { EducationSystemsOverviewPageWithMap } from "../views/pages/qualCompare/EducationSystemsOverviewPageWithMap";

const routes = [
  {
    path: "/",
    element: <PortalLayout hideSubHeader />,
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      { path: "/home", element: <HomePage /> },
    ],
  },
  {
    path: "/",
    element: <PortalLayout />,
    children: [
      {
        path: "/sitemap",
        element: <Sitemap />,
      },
      {
        path: "/contacts",
        element: <Contacts />,
      },
      {
        path: "/law",
        element: <Law />,
      },
      {
        path: "personal-content",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />}>
            <PersonalContent />
          </SecurityGuard>
        ),
      },
      {
        path: "/registration",
        element: (
          <AnonymousGuard displayOnAuthenticated={<Page403 />}>
            <Registration />
          </AnonymousGuard>
        ),
      },
      {
        path: "/my-account",
        element: (
          <AuthenticationGuard displayOnUnauthenticated={<Page401 />}>
            <MyAccountManagement />
          </AuthenticationGuard>
        ),
      },
      {
        path: "feedback",
        element: <Feedback />,
      },
      {
        path: "forgotten-password",
        element: (
          <AnonymousGuard displayOnAuthenticated={<Page403 />}>
            <ForgottenPassword />
          </AnonymousGuard>
        ),
      },
      {
        path: "forgotten-username",
        element: (
          <AnonymousGuard displayOnAuthenticated={<Page403 />}>
            <ForgottenUsername />
          </AnonymousGuard>
        ),
      },
      {
        path: "activation-link",
        element: (
          <AnonymousGuard displayOnAuthenticated={<Page403 />}>
            <ActivationLink />
          </AnonymousGuard>
        ),
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "account-activation",
        element: <AccountActivation />,
      },
      {
        path: "change-email",
        element: <EmailChange />,
      },
      {
        path: "qual-compare",
        element: <EducationSystemsOverviewPageWithMap />,
      },
    ],
  },
  {
    path: "/admin-console",
    element: <AdminConsoleLayout />,
    children: [
      {
        index: true,
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcAccess]}>
            <AdminConsole />
          </SecurityGuard>
        ),
      },
      {
        path: "cache",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.FoCacheAccess]}>
            <CachePage />
          </SecurityGuard>
        ),
      },
      {
        path: "config",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcEdit]}>
            <AdminConsoleManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "sitemap",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcSitemapAccess]}>
            <SiteMapEdit />
          </SecurityGuard>
        ),
      },
      {
        path: "home-page-category",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcHomePageCategoriesAccess]}>
            <HomePageCategoryManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "service-definitions",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.ServiceDefinitionAccess]}>
            <ServiceDefinitionsManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "feedback",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcFeedbacksAccess]}>
            <FeedbackManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "global-message",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcGlobalMessageAccess]}>
            <GlobalMessageManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "email/notifications",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcEmailNotificationsAccess]}>
            <EmailNotificationsManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "email/templates",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcEmailTemplatesAccess]}>
            <EmailTemplatesManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "reference-data",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcJoinedNomenclaturesAccess]}>
            <ReferenceDataManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "national-university",
        element: (
          <SecurityGuard
            displayOnUnauthorized={<Page403 />}
            checkForRoles={[SecurityRole.AcNationalUniversitiesAccess]}
          >
            <NationalUniversityManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "contacts",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcContactsAccess]}>
            <ContactsManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "users",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcUsersAccess]}>
            <UsersManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "users/edit",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcUsersEdit]}>
            <AdminUserEdit />
          </SecurityGuard>
        ),
      },
      {
        path: "users/view",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcUsersAccess]}>
            <AdminUserView />
          </SecurityGuard>
        ),
      },
      {
        path: "groups-roles",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcGroupsAndRolesAccess]}>
            <GroupsAndRolesManagement />
          </SecurityGuard>
        ),
      },
      {
        path: "law",
        element: (
          <SecurityGuard displayOnUnauthorized={<Page403 />} checkForRoles={[SecurityRole.AcLawAccess]}>
            <LawManagement />
          </SecurityGuard>
        ),
      },
    ],
  },
  {
    path: "*",
    element: <PortalLayout />,
    children: [
      {
        path: "*",
        element: <Page404 />,
      },
    ],
  },
];

export default routes;
